import React, { useState, useEffect } from 'react';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import Icon from '../components/icons/Icon';
import { useAnalytics } from '../hooks/useAnalytics';
import './Templates.css';

const Templates = () => {
  const { trackAction, trackTemplate, trackSearch } = useAnalytics();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async (tenant) => {
      try {
        setLoading(true);
        setError(null);
        
        if (tenant?.tenantId) {
          const result = await miaPlatformDataService.getTemplates(tenant.tenantId);
          setTemplates(result || []);
        } else {
          setTemplates([]);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    // Initial load
    const initialTenant = miaFlowAuth.getSelectedTenant();
    setSelectedTenant(initialTenant);
    fetchTemplates(initialTenant);

    // Listen for changes
    const handleTenantChange = (event) => {
      const newTenant = event.detail.tenant;
      setSelectedTenant(newTenant);
      fetchTemplates(newTenant);
    };
    
    window.addEventListener('mia-flow-tenant-changed', handleTenantChange);
    
    return () => {
      window.removeEventListener('mia-flow-tenant-changed', handleTenantChange);
    };
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 2) {
      const results = templates.filter(template => 
        template.name?.toLowerCase().includes(query.toLowerCase()) ||
        template.description?.toLowerCase().includes(query.toLowerCase())
      );
      trackSearch(query, { 
        context: 'templates',
        resultCount: results.length 
      });
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesCategory = true;
    if (selectedCategory !== 'all') {
      // Add category filtering logic here if needed
      matchesCategory = template.category === selectedCategory;
    }
    
    return matchesSearch && matchesCategory;
  });

  // Handle category change with analytics
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    trackAction('template', 'category_changed', { category });
  };

  // Handle template selection
  const handleSelectTemplate = (template) => {
    trackTemplate(template._id || template.id, {
      templateName: template.name,
      templateCategory: template.category,
      action: 'selected'
    });
    trackFeature('template_usage', {
      templateName: template.name,
      category: template.category
    });
    console.log('Selected template:', template);
  };

  return (
    <div className="templates-page">
      {/* Header — matches Catalog style */}
      <div className="templates-header">
        <div className="templates-title-section">
          <h1 className="templates-title">Templates</h1>
          <p className="templates-subtitle">Pre-built projects ready to customize</p>
        </div>
      </div>

      {/* Controls bar — search left, filters right, below header like Catalog nav */}
      <div className="templates-controls-bar">
        <div className="templates-controls">
          <div className="templates-search">
            <Icon name="search" className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={handleSearch}
              className="templates-search-input"
            />
          </div>

          <div className="templates-filters">
            <button
              className={`filter-tab ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              All templates
            </button>
            <button
              className={`filter-tab ${selectedCategory === 'web' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('web')}
            >
              Web Apps
            </button>
            <button
              className={`filter-tab ${selectedCategory === 'api' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('api')}
            >
              APIs
            </button>
            <button
              className={`filter-tab ${selectedCategory === 'microservices' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('microservices')}
            >
              Microservices
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="templates-body">

      {loading && (
        <div className="templates-loading">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      )}

      {error && (
        <div className="templates-error">
          <p>❌ {error}</p>
        </div>
      )}

      {!loading && !error && !selectedTenant && (
        <div className="templates-empty">
          <Icon name="template" size={64} className="empty-icon" />
          <h3>No tenant selected</h3>
          <p>Please select a tenant from the workspace selector to view templates</p>
        </div>
      )}

      {!loading && !error && selectedTenant && (
        <div className="templates-grid">
          {filteredTemplates.length === 0 ? (
            <div className="templates-empty">
              <Icon name="template" size={64} className="empty-icon" />
              <h3>No templates found</h3>
              <p>Try adjusting your search or check back later</p>
            </div>
          ) : (
            filteredTemplates.map((template, idx) => (
              <div 
                key={template._id || idx} 
                className="template-card"
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="template-preview">
                  {template.preview || template.thumbnail ? (
                    <img 
                      src={template.preview || template.thumbnail} 
                      alt={template.name} 
                      className="template-thumbnail" 
                    />
                  ) : (
                    <div className="template-placeholder">
                      <Icon name="template" size={48} />
                    </div>
                  )}
                </div>
                
                <div className="template-info">
                  <h3 className="template-name">{template.name}</h3>
                  <p className="template-description">
                    {template.description || 'No description available'}
                  </p>
                  
                  {template.tags && template.tags.length > 0 && (
                    <div className="template-tags">
                      {template.tags.slice(0, 3).map((tag, tagIdx) => (
                        <span key={tagIdx} className="template-tag">{tag}</span>
                      ))}
                      {template.tags.length > 3 && (
                        <span className="template-tag-more">+{template.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      </div>{/* end templates-body */}
    </div>
  );
};

export default Templates;
